import { useCallback, useEffect, useState } from "react";
import TrackTile from "./TrackSelectionTile";
import miniTrackImage from "../../assets/images/miniTrack_.jpeg";
import mainTrackImage from "../../assets/images/main_track_.jpg";
import vrImage from "../../assets/images/virtualReality.jpg";
import Navigation from "../App/Navigation";
import { ROUTES, TRACKS } from "../../consts";
import {
  TTrackTypes,
  TMetaObjectBannerSection,
  TMetaObjectMainTrackConfig,
  TMetaObjectMiniTrackConfig,
  TMetaObjectVRConfig,
  TUpsellModalConfig,
  TMetaObjectSeatsAndRacesConfig,
} from "../../types";
import { useTrackContext } from "../../hooks/useTrackContext";
import ProgressBar from "../ProgressiveBar";
import { useLocation, useNavigate } from "react-router-dom";
import MiniCart from "../Cart/MiniCart";
import loadingImageGif from "./../../assets/images/loadingScreenAnimation.gif";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../config";
import { GET_METAOBJECT_BY_HANDLE } from "../../graphqlQueries";
const currentHref = window.location.pathname;

const TrackSelection = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const paramsNumberOfSeats = searchParams.get("numberOfSeats");
  const paramsNumberOfRaces = searchParams.get("numberOfRaces");
  const paramsAgeGroup = searchParams.get("ageGroup");
  const paramsHomepage = searchParams.get("homepage");
  const [isRacingGamesTrack, setIsRacingGamesTrack] = useState<boolean>(false);
  const paramsRedirect = searchParams.get("redirect");
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();

  const {
    dispatch,
    productRenderedLocation,
    trackSelectionOption: { title },
    windowWidth,
    metaObjectTrackSelectionConfig,
    appHostedAt,
    isFirstVisit,
  } = useTrackContext();

  const [selectedTrack, setSelectedTrack] = useState<TTrackTypes>(title as TTrackTypes);
  const toNavigation = useCallback(() => {
    switch (selectedTrack) {
      case TRACKS.MAIN_TRACK:
        if (paramsNumberOfSeats && paramsNumberOfRaces && paramsHomepage && paramsHomepage === "false") {
          return ROUTES.MAIN_TRACK.DATE_TIME;
        } else {
          return ROUTES.MAIN_TRACK.AGE_GROUP;
        }
      case TRACKS.MINI_TRACK:
        return ROUTES.MINI_TRACK.SEATS_RACES;
      case TRACKS.VIRTUAL_REALITY:
        return ROUTES.VIRTUAL_REALITY.PLAYERS_SESSIONS;
      case TRACKS.JUNIOR_TRACK:
        if (paramsNumberOfSeats && paramsNumberOfRaces && paramsHomepage && paramsHomepage === "false") {
          return ROUTES.JUNIOR_TRACK.DATE_TIME;
        } else {
          return ROUTES.JUNIOR_TRACK.SEATS_RACES;
        }
      case TRACKS.BATTLE_KART_TRACK:
        return ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS;
    }
  }, [paramsHomepage, paramsNumberOfRaces, paramsNumberOfSeats, selectedTrack]);

  const handleDoubleRedirect = (url: string) => {
    return navigate(url);
  };

  const handleTrackSelection = (track: TTrackTypes, imageSrc: string, url: string) => {
    setSelectedTrack(track);
    dispatch({
      type: "SET_SELECTED_TRACK",
      payload: {
        selectedTrackTitle: track,
        trackBanner: imageSrc,
      },
    });

    if (track === "RACING_GAMES_TRACK" && title === "RACING_GAMES_TRACK") {
      setIsRacingGamesTrack(true);
      setSelectedTrack("MAIN_TRACK");
      dispatch({
        type: "SET_SELECTED_TRACK",
        payload: {
          selectedTrackTitle: "MAIN_TRACK",
          trackBanner: mainTrackImage,
        },
      });
    } else {
      if (title === track) {
        return navigate(url);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query: GET_METAOBJECT_BY_HANDLE,
            variables: {
              handle: "product-react-component-config",
              type: "product_react_component_config",
            },
          }),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const receivedData = await response.json();
        const fields = receivedData.data.metaobject.fields;
        const [value] = fields;

        const [{ bannerSection, mainTrackConfig, miniTrackConfig, virtualRealityConfig, upsellModalConfig, juniorTrackConfig, battleKartConfig }] = JSON.parse(value.value) as {
          bannerSection: TMetaObjectBannerSection;
          mainTrackConfig: TMetaObjectMainTrackConfig;
          miniTrackConfig: TMetaObjectMiniTrackConfig;
          virtualRealityConfig: TMetaObjectVRConfig;
          upsellModalConfig: TUpsellModalConfig;
          juniorTrackConfig: TMetaObjectSeatsAndRacesConfig;
          battleKartConfig: TMetaObjectSeatsAndRacesConfig;
        }[];

        if (bannerSection && mainTrackConfig && miniTrackConfig && virtualRealityConfig && upsellModalConfig && juniorTrackConfig && battleKartConfig) {
          dispatch({
            type: "SET_METAOJECT_BATTLE_KART_TRACK_CONFIG",
            payload: {
              metaObjectBattleKartTrackSectionConfig: battleKartConfig as TMetaObjectSeatsAndRacesConfig,
            },
          });

          dispatch({
            type: "SET_METAOBJECT_TRACK_SELECTION_CONFIG",
            payload: {
              metaObjectTrackSelectionConfig: bannerSection as TMetaObjectBannerSection,
            },
          });

          dispatch({
            type: "SET_METAOJECT_MAIN_TRACK_CONFIG",
            payload: {
              metaObjectMainTrackSectionConfig: mainTrackConfig as TMetaObjectMainTrackConfig,
            },
          });

          dispatch({
            type: "SET_METAOJECT_MINI_TRACK_CONFIG",
            payload: {
              metaObjectMiniTrackSectionConfig: miniTrackConfig as TMetaObjectMiniTrackConfig,
            },
          });

          dispatch({
            type: "SET_METAOJECT_VIRTUAL_REALITY_CONFIG",
            payload: {
              metaObjectVRSectionConfig: virtualRealityConfig as TMetaObjectVRConfig,
            },
          });

          dispatch({
            type: "SET_METAOJECT_UPSELL_MODAL_CONFIG",
            payload: {
              upsellModalConfig: upsellModalConfig as TUpsellModalConfig,
            },
          });

          dispatch({
            type: "SET_METAOJECT_JUNIOR_TRACK_CONFIG",
            payload: {
              metaObjectJuniorTrackSectionConfig: juniorTrackConfig as TMetaObjectSeatsAndRacesConfig,
            },
          });
          const { seatsAndRacesSection } = mainTrackConfig;
          const { seatsAndRacesSection: juniorTrackSeatsAndRacesSection } = juniorTrackConfig;

          if (seatsAndRacesSection) {
            if (paramsNumberOfSeats && paramsNumberOfRaces) {
              dispatch({
                type: "SET_RACES_SEATS",
                payload: {
                  selectedTrack: "MAIN_TRACK",
                  racesSeats: {
                    numberOfRaces: parseInt(paramsNumberOfRaces),
                    numberOfSeats: parseInt(paramsNumberOfSeats),
                  },
                },
              });
              if (paramsAgeGroup) {
                dispatch({
                  type: "SET_AGE_GROUP",
                  payload: {
                    selectedTrack: "MAIN_TRACK",
                    ageGroup: "Under 12",
                  },
                });
              }
            } else {
              dispatch({
                type: "SET_RACES_SEATS",
                payload: {
                  selectedTrack: "MAIN_TRACK",
                  racesSeats: {
                    numberOfRaces: seatsAndRacesSection.raceQuantity,
                    numberOfSeats: seatsAndRacesSection.seatsQuantity,
                  },
                },
              });
            }
          }

          if (juniorTrackSeatsAndRacesSection) {
            if (paramsNumberOfSeats && paramsNumberOfRaces) {
              dispatch({
                type: "SET_RACES_SEATS_JUNIOR_TRACK",
                payload: {
                  racesSeats: {
                    numberOfRaces: parseInt(paramsNumberOfRaces),
                    numberOfSeats: parseInt(paramsNumberOfSeats),
                  },
                },
              });
            } else {
              dispatch({
                type: "SET_RACES_SEATS_JUNIOR_TRACK",
                payload: {
                  racesSeats: {
                    numberOfRaces: juniorTrackSeatsAndRacesSection.raceQuantity,
                    numberOfSeats: juniorTrackSeatsAndRacesSection.seatsQuantity,
                  },
                },
              });
            }
          }

          if (miniTrackConfig.seatsAndRacesSection) {
            const { raceQuantity, seatsQuantity } = miniTrackConfig.seatsAndRacesSection;
            if (raceQuantity && seatsQuantity) {
              dispatch({
                type: "SET_RACES_SEATS_MINI_TRACK",
                payload: {
                  selectedTrack: "MINI_TRACK",
                  racesSeats: {
                    numberOfRaces: raceQuantity,
                    numberOfSeats: seatsQuantity,
                  },
                },
              });
            }
          }

          const {
            playersAndSessionsSection: { playerQuantity, sessionQuantity },
          } = virtualRealityConfig;

          if (playerQuantity && sessionQuantity) {
            dispatch({
              type: "SET_PLAYERS_SESSIONS_VIRTUAL_REALITY",
              payload: {
                playersAndSessions: {
                  numberOfPlayers: playerQuantity,
                  numberOfSessions: sessionQuantity,
                },
              },
            });
          }

          if (battleKartConfig.seatsAndRacesSection) {
            const { raceQuantity, seatsQuantity } = battleKartConfig.seatsAndRacesSection;
            if (raceQuantity && seatsQuantity) {
              dispatch({
                type: "SET_RACES_SEATS_BATTLE_KART_TRACK",
                payload: {
                  racesSeats: {
                    numberOfRaces: raceQuantity,
                    numberOfSeats: seatsQuantity,
                  },
                },
              });
            }
          }

          if (location.pathname === "/" && isFirstVisit && paramsRedirect === "true") {
            dispatch({
              type: "SET_IS_FIRST_VISIT",
              payload: {
                isFirstVisit: false,
              },
            });

            if (currentHref.endsWith("/main-go-karting-track") || /\d{6}mt$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "MAIN_TRACK",
                  trackBanner: mainTrackImage,
                },
              });
              navigate(ROUTES.MAIN_TRACK.AGE_GROUP);
            } else if (currentHref.endsWith("/kids-go-karting-track") || /\d{7}mi$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "MINI_TRACK",
                  trackBanner: miniTrackImage,
                },
              });
              navigate(ROUTES.MINI_TRACK.SEATS_RACES);
            } else if (currentHref.endsWith("/hyper-reality-vr-arcade") || /\d{6}vr$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "VIRTUAL_REALITY",
                  trackBanner: vrImage,
                },
              });
              navigate(ROUTES.VIRTUAL_REALITY.PLAYERS_SESSIONS);
            } else if (currentHref.endsWith("/junior-track") || /\d{6}jt$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "JUNIOR_TRACK",
                  trackBanner: vrImage,
                },
              });
              navigate(ROUTES.JUNIOR_TRACK.SEATS_RACES);
            } else if (currentHref.endsWith("/pages/battle-kart-booking") || /\d{6}bk$/.test(currentHref)) {
              dispatch({
                type: "SET_SELECTED_TRACK",
                payload: {
                  selectedTrackTitle: "BATTLE_KART_TRACK",
                  trackBanner: mainTrackImage,
                },
              });
              navigate(ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS);
            }
          }
        }

        if (paramsNumberOfSeats && paramsNumberOfRaces) {
          if (paramsHomepage && paramsHomepage === "false" && selectedTrack === "MAIN_TRACK") {
            navigate(ROUTES.MAIN_TRACK.DATE_TIME);
          } else if (paramsHomepage && paramsHomepage === "false" && selectedTrack === "JUNIOR_TRACK") {
            navigate(ROUTES.JUNIOR_TRACK.DATE_TIME);
          }
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch, location.pathname, navigate, paramsAgeGroup, paramsHomepage, paramsNumberOfRaces, paramsNumberOfSeats, selectedTrack, toNavigation, isFirstVisit, paramsRedirect]);

  const { mainTrack, miniTrack, virtualReality, giftCardButton, juniorTrack, trackSelectionScreen, racingGamesTrack, battleKartTrack } = metaObjectTrackSelectionConfig;

  const handlePreviousStatus = () => {
    if (isRacingGamesTrack) {
      setIsRacingGamesTrack(false);
      setSelectedTrack("RACING_GAMES_TRACK");
      dispatch({
        type: "SET_SELECTED_TRACK",
        payload: {
          selectedTrackTitle: "RACING_GAMES_TRACK",
          trackBanner: mainTrackImage,
        },
      });
    }
  };

  const handleNextStatus = () => {
    if (selectedTrack === "RACING_GAMES_TRACK") {
      setIsRacingGamesTrack(true);
      setSelectedTrack("MAIN_TRACK");
      dispatch({
        type: "SET_SELECTED_TRACK",
        payload: {
          selectedTrackTitle: "MAIN_TRACK",
          trackBanner: mainTrackImage,
        },
      });
    }
  };

  return (
    <>
      <ProgressBar
        maxValue={100}
        currentValue={
          title === "MAIN_TRACK"
            ? windowWidth <= 425 || (windowWidth >= 425 && windowWidth <= 768)
              ? 20
              : 15
            : title === "MINI_TRACK"
            ? 22
            : title === "VIRTUAL_REALITY" && (windowWidth <= 425 || (windowWidth >= 425 && windowWidth <= 768))
            ? 18
            : title === "RACING_GAMES_TRACK"
            ? 40
            : title === "BATTLE_KART_TRACK"
            ? 25
            : 25
        }
      />
      {loading ? (
        <>
          <div className="container tw-flex tw-justify-center tw-items-center tw-mt-[100px] tw-mb-[100px]">
            <img src={"https://hyperkarting-product-component.vercel.app" + loadingImageGif} alt="" className="tw-w-[230px] md:tw-w-[300px] tw-h-full" />
          </div>
        </>
      ) : (
        <>
          {productRenderedLocation === "external" && <MiniCart />}
          {trackSelectionScreen && (
            <div className="tw-block lg:tw-hidden">
              <div className="tw-mt-[30px] tw-flex tw-flex-col tw-gap-[10px] tw-items-center tw-justify-center">
                {!isRacingGamesTrack && trackSelectionScreen.title && <span className="tw-text-white tw-text-[24px] tw-font-extrabold">{trackSelectionScreen.title}</span>}
                {!isRacingGamesTrack && trackSelectionScreen.subtitle && <span className="tw-text-[#F609FF] tw-text-[18px] tw-font-semibold">{trackSelectionScreen.subtitle}</span>}
                {/* Racing track selection screen headings commented out */}
                {/* {isRacingGamesTrack && trackSelectionScreen.title && <span className="tw-text-white tw-text-[24px] tw-font-extrabold">{trackSelectionScreen.title}</span>} */}
                {/* {isRacingGamesTrack && trackSelectionScreen.subtitle && <span className="tw-text-[#F609FF] tw-text-[18px] tw-font-semibold">{trackSelectionScreen.subtitle}</span>} */}
              </div>
            </div>
          )}
          <div className="tw-hidden lg:tw-block">
            {!isRacingGamesTrack && selectedTrack === "RACING_GAMES_TRACK" && (
              <Navigation
                previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                next={{ active: true, to: `/${toNavigation()}`, handleCallback: handleNextStatus }}
                title={trackSelectionScreen?.title}
                subtitle={trackSelectionScreen?.subtitle}
              />
            )}
            {/* Racing track selection screen Navigation commented out */}
            {/* {isRacingGamesTrack && selectedTrack === "RACING_GAMES_TRACK" && (
              <Navigation
                previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                next={{ active: true, to: `/${toNavigation()}`, handleCallback: handleNextStatus }}
                title={trackSelectionScreen?.title}
                subtitle={trackSelectionScreen?.subtitle}
              />
            )} */}

            {!isRacingGamesTrack && (selectedTrack === "MAIN_TRACK" ||
              selectedTrack === "MINI_TRACK" ||
              selectedTrack === "VIRTUAL_REALITY" ||
              selectedTrack === "JUNIOR_TRACK" ||
              selectedTrack === "BATTLE_KART_TRACK") && (
              <Navigation
                previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                next={{ active: true, to: `/${toNavigation()}` }}
                title={trackSelectionScreen?.title}
                subtitle={trackSelectionScreen?.subtitle}
              />
            )}
            {/* Racing track selection screen Navigation commented out */}
            {/* {isRacingGamesTrack && (selectedTrack === "MAIN_TRACK" ||
              selectedTrack === "MINI_TRACK" ||
              selectedTrack === "VIRTUAL_REALITY" ||
              selectedTrack === "JUNIOR_TRACK" ||
              selectedTrack === "BATTLE_KART_TRACK") && (
              <Navigation
                previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                next={{ active: true, to: `/${toNavigation()}` }}
                title={trackSelectionScreen?.title}
                subtitle={trackSelectionScreen?.subtitle}
              />
            )} */}
          </div>

          <div className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"}`}>
            {appHostedAt && appHostedAt === "bosshunting" ? (
              <>
                <div className="tw-flex tw-justify-center tw-items-center">
                  {mainTrack && title === "MAIN_TRACK" && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.MAIN_TRACK}
                      title={mainTrack.title.toLowerCase()}
                      description={mainTrack.description}
                      image={mainTrackImage}
                      videoBannerUrl={mainTrack.video_banner_url}
                    />
                  )}

                  {miniTrack && title === "MINI_TRACK" && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.MINI_TRACK}
                      title={miniTrack.title.toLowerCase()}
                      description={miniTrack.description}
                      image={miniTrackImage}
                      videoBannerUrl={miniTrack.video_banner_url}
                    />
                  )}

                  {virtualReality && title === "VIRTUAL_REALITY" && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("VIRTUAL_REALITY", vrImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.VIRTUAL_REALITY}
                      title={virtualReality.title.toLowerCase()}
                      description={virtualReality.description}
                      image={vrImage}
                      videoBannerUrl={virtualReality.video_banner_url}
                    />
                  )}

                  {juniorTrack && title === "JUNIOR_TRACK" && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("JUNIOR_TRACK", vrImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.JUNIOR_TRACK}
                      title={juniorTrack.title.toLowerCase()}
                      description={juniorTrack.description}
                      image={vrImage}
                      videoBannerUrl={juniorTrack.video_banner_url}
                    />
                  )}
                </div>

                <div className="tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[15px] tw-mt-[30px]">
                  <span className="tw-text-[white] tw-font-[700] tw-text-center tw-flex">
                    {title === "MAIN_TRACK" ? "Main Track" : title === "MINI_TRACK" ? "Mini Track" : "Virtual Reality"} Selected{" "}
                  </span>
                </div>

                <div className={`${windowWidth >= 640 ? "tw-flex tw-justify-center tw-items-center" : ""} tw-gap-[10px] tw-my-[20px]`}>
                  <button
                    type="button"
                    onClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage, `/${toNavigation()}`)}
                    style={{ width: "200px" }}
                    className={`tw-rounded-[100px] tw-block ${
                      windowWidth >= 640 ? "tw-mx-[0]" : "tw-mx-auto"
                    } tw-text-center tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                      title === "MAIN_TRACK" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                    }`}
                  >
                    Main Track
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage, `/${toNavigation()}`)}
                    style={{ width: "200px" }}
                    className={`tw-rounded-[100px] tw-block ${
                      windowWidth >= 640 ? "tw-mx-[0] tw-my-[0]" : "tw-mx-auto"
                    } tw-text-center tw-my-[10px] tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                      title === "MINI_TRACK" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                    }`}
                  >
                    Mini Track
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTrackSelection("VIRTUAL_REALITY", vrImage, `/${toNavigation()}`)}
                    style={{ width: "200px" }}
                    className={`tw-rounded-[100px] tw-block ${
                      windowWidth >= 640 ? "tw-mx-[0]" : "tw-mx-auto"
                    } tw-text-center tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                      title === "VIRTUAL_REALITY" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                    }`}
                  >
                    Virtual Reality
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-[30px] lg:tw-gap-[50px] tw-my-[30px] lg:tw-mt-[120px] lg:tw-mb-[40px]">
                  {!isRacingGamesTrack && racingGamesTrack && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("RACING_GAMES_TRACK", mainTrackImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.RACING_GAMES_TRACK}
                      title={racingGamesTrack.title.toLowerCase()}
                      description={racingGamesTrack.description}
                      image={mainTrackImage}
                      videoBannerUrl={racingGamesTrack.video_banner_url}
                    />
                  )}
                  {!isRacingGamesTrack && battleKartTrack && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("BATTLE_KART_TRACK", mainTrackImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.BATTLE_KART_TRACK}
                      title={battleKartTrack.title.toLowerCase()}
                      description={battleKartTrack.description}
                      image={mainTrackImage}
                      videoBannerUrl={battleKartTrack.video_banner_url}
                    />
                  )}

                  {isRacingGamesTrack && mainTrack && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.MAIN_TRACK}
                      title={mainTrack.title.toLowerCase()}
                      description={mainTrack.description}
                      image={mainTrackImage}
                      videoBannerUrl={mainTrack.video_banner_url}
                    />
                  )}

                  {isRacingGamesTrack && juniorTrack && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("JUNIOR_TRACK", vrImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.JUNIOR_TRACK}
                      title={juniorTrack.title.toLowerCase()}
                      description={juniorTrack.description}
                      image={vrImage}
                      videoBannerUrl={juniorTrack.video_banner_url}
                      badge="New"
                    />
                  )}

                  {isRacingGamesTrack && miniTrack && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.MINI_TRACK}
                      title={miniTrack.title.toLowerCase()}
                      description={miniTrack.description}
                      image={miniTrackImage}
                      videoBannerUrl={miniTrack.video_banner_url}
                    />
                  )}

                  {!isRacingGamesTrack && virtualReality && (
                    <TrackTile
                      onDoubleClick={() => handleDoubleRedirect(`/${toNavigation()}`)}
                      onClick={() => handleTrackSelection("VIRTUAL_REALITY", vrImage, `/${toNavigation()}`)}
                      isSelected={selectedTrack === TRACKS.VIRTUAL_REALITY}
                      title={virtualReality.title.toLowerCase()}
                      description={virtualReality.description}
                      image={vrImage}
                      videoBannerUrl={virtualReality.video_banner_url}
                    />
                  )}
                </div>
              </>
            )}

            {giftCardButton && (
              <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-center md:tw-mb-[40px] tw-items-center tw-gap-[20px] tw-items-center">
                <p
                  className="tw-text-white tw-text-[18px] tw-font-semibold"
                  style={appHostedAt && appHostedAt === "bosshunting" ? { width: "unset", height: "unset", margin: "unset", fontWeight: 600 } : {}}
                >
                  {giftCardButton.buttonDescription}
                </p>
                <a
                  href={giftCardButton.buttonUrl}
                  className="tw-rounded-[100px] tw-mb-[60px] md:tw-mb-0 tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-text-white tw-font-bold tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]"
                  style={appHostedAt && appHostedAt === "bosshunting" ? { textDecoration: "none", width: "unset", height: "unset", margin: "unset", fontWeight: 700 } : {}}
                >
                  {giftCardButton.buttonText}
                </a>
              </div>
            )}
          </div>
          <div className="tw-w-full lg:tw-h-[126px] tw-p-[30px]">
            <div className="tw-flex tw-justify-center tw-items-center tw-block lg:tw-hidden">
              {!isRacingGamesTrack && selectedTrack === "RACING_GAMES_TRACK" && (
                <Navigation
                  previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                  next={{ active: true, to: `/${toNavigation()}`, handleCallback: handleNextStatus }}
                  title={trackSelectionScreen?.title}
                  subtitle={trackSelectionScreen?.subtitle}
                />
              )}
              {/* Racing track selection screen Navigation commented out */}
              {/* {isRacingGamesTrack && selectedTrack === "RACING_GAMES_TRACK" && (
                <Navigation
                  previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                  next={{ active: true, to: `/${toNavigation()}`, handleCallback: handleNextStatus }}
                  title={trackSelectionScreen?.title}
                  subtitle={trackSelectionScreen?.subtitle}
                />
              )} */}

              {!isRacingGamesTrack && (selectedTrack === "MAIN_TRACK" ||
                selectedTrack === "MINI_TRACK" ||
                selectedTrack === "VIRTUAL_REALITY" ||
                selectedTrack === "JUNIOR_TRACK" ||
                selectedTrack === "BATTLE_KART_TRACK") && (
                <Navigation
                  previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                  next={{ active: true, to: `/${toNavigation()}` }}
                  title={trackSelectionScreen?.title}
                  subtitle={trackSelectionScreen?.subtitle}
                />
              )}
              {/* Racing track selection screen Navigation commented out */}
              {/* {isRacingGamesTrack && (selectedTrack === "MAIN_TRACK" ||
                selectedTrack === "MINI_TRACK" ||
                selectedTrack === "VIRTUAL_REALITY" ||
                selectedTrack === "JUNIOR_TRACK" ||
                selectedTrack === "BATTLE_KART_TRACK") && (
                <Navigation
                  previous={{ active: isRacingGamesTrack, to: ``, handleCallback: handlePreviousStatus }}
                  next={{ active: true, to: `/${toNavigation()}` }}
                  title={trackSelectionScreen?.title}
                  subtitle={trackSelectionScreen?.subtitle}
                />
              )} */}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TrackSelection;
